import React from 'react';
import { canEditFinalAdvance } from '../utils/otrCalculations';

export const AdvanceRequestBox = ({
  advanceRequested,
  advance,
  onRequestedAmountChange,
  onAdvanceChange,
  isEditable = false,       // Employee editing the initial request (Draft / Sent Back)
  approvalFlow = '',        // APPROVAL_FLOW.MANAGER | FINANCE | MANAGER_THEN_FINANCE | FINANCE_THEN_MANAGER
  currentUserRole = '',     // 'manager' | 'finance' (role of the person viewing this request)
  status = '',
  canEditAdvance = false,   // Backend/permission driven: manager or finance acting on the request
  currencySymbol = '₹'
}) => {
  // Whether the current viewer is allowed to edit the Final Advance amount
  // is determined purely by the tenant's approval flow + the viewer's role -
  // NOT by which approval stage (L1/L2) happens to be active. See
  // utils/otrCalculations.js#canEditFinalAdvance for the full rule.
  const editableFinalAdvance =
    canEditAdvance || canEditFinalAdvance(approvalFlow, currentUserRole, status);

  // Show the Final Advance section once the request has left Draft, either
  // because the viewer can edit it now, or because a value already exists
  // to display read-only.
  const showFinalAdvance =
    editableFinalAdvance || (advance !== undefined && advance !== null && advance > 0);

  // Read-only view: the requested amount is already shown in Trip Details /
  // Expense Summary, so the box only appears when there is a Final Advance to show/edit.
  if (!isEditable && !showFinalAdvance) return null;

  return (
    <div className="p-4 sm:p-6 bg-white rounded-2xl border border-[var(--color-border)] shadow-sm">
      <div className="flex flex-col gap-4">
        {isEditable && (
        <div>
          <label className="block text-[11px] font-bold uppercase tracking-wider text-[var(--color-text-secondary)] mb-1.5">
            {isEditable ? 'Advance Request' : 'Advance Requested'}
          </label>
          {isEditable ? (
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm font-bold text-[var(--color-text-secondary)]">{currencySymbol}</span>
              <input
                type="number"
                value={advanceRequested || ''}
                placeholder="Enter advance amount"
                onChange={(e) => onRequestedAmountChange(parseFloat(e.target.value) || 0)}
                className="w-full h-11 pl-8 pr-4 text-sm font-bold rounded-xl border border-[var(--color-border)] bg-white outline-none transition-all focus:ring-2 focus:ring-[var(--color-primary)]/20 placeholder:font-normal"
              />
            </div>
          ) : (
            <div className="text-sm font-bold text-foreground">
              {advanceRequested ? `${currencySymbol} ${advanceRequested.toLocaleString()}` : '—'}
            </div>
          )}
          <div className="text-[11px] text-[var(--color-text-secondary)] mt-1.5">
            {isEditable ? 'Optional. Enter the amount you want to request from Finance.' : 'Requested by the employee.'}
          </div>
        </div>
        )}

        {showFinalAdvance && (
          <div className={isEditable ? "pt-4 border-t border-[var(--color-border)]" : ""}>
            <label className="block text-[11px] font-bold uppercase tracking-wider text-[var(--color-text-secondary)] mb-1.5">
              Advance Paid
            </label>
            {editableFinalAdvance ? (
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm font-bold text-[var(--color-text-secondary)]">{currencySymbol}</span>
                <input
                  type="number"
                  min="0"
                  value={advance || ''}
                  placeholder="Enter advance paid"
                  onChange={(e) => onAdvanceChange(Math.max(0, parseFloat(e.target.value) || 0))}
                  className="w-full h-11 pl-8 pr-4 text-sm font-bold rounded-xl border border-[var(--color-border)] bg-white outline-none transition-all focus:ring-2 focus:ring-[var(--color-primary)]/20"
                />
              </div>
            ) : (
              <div className="text-sm font-bold text-foreground">
                {advance ? `${currencySymbol} ${advance.toLocaleString()}` : '—'}
              </div>
            )}
            <div className="text-[11px] text-[var(--color-text-secondary)] mt-1.5">
              {editableFinalAdvance
                ? 'Enter the advance amount paid. It is saved when you approve.'
                : 'Advance paid is entered by the approver.'}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};